import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { useSmartNavigationData } from '../../onboarding/hooks/use-smart-navigation-data';
import { SuperLink } from './link';
import { TranslationNamespaces } from '~/lib/constants';
import useGoto from '~/hooks/use-goto';
import { useTranslation } from '@parcel2go/npm-p2g-app';
import { useMemo } from 'react';
import { P2GDashboardTabProps } from '~/components/dashboard/dash-tile/tab';
import { sanitisedRoutesOfOrders } from '~/routes/groups/orders';
import RenderSection from './section';
import { SvgParcel } from '@parcel2go/core-ui';

const OrderNavigationSection = ({
  currentPageRoute,
  logEvent
}: {
  currentPageRoute: string;
  logEvent: (link: string, close?: boolean) => void;
}) => {
  const { soldCount, inTransitCount, bookedCount } = useSmartNavigationData();
  const { goTo } = useGoto();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const soldItemsTile = useMemo(() => {
    return {
      onClick: () => {
        logEvent('sold-items');
        goTo(RoutingConstants.SmartSend);
      },
      key: RoutingConstants.SmartSend,
      selected: currentPageRoute === RoutingConstants.SmartSend,
      title: t('ORDER_AREA_TAB_NEW_SMARTSEND_ORDERS', {
        defaultValue: 'New SmartSend Orders ({{value}})',
        value: soldCount
      }),
      type: 'NUMBER',
      value: soldCount || ('...' as any)
    };
  }, [currentPageRoute, t, logEvent, goTo, soldCount]);

  const inTransitTile = useMemo(() => {
    return {
      onClick: () => {
        logEvent('intransit-items');
        goTo(RoutingConstants.InTransit);
      },
      key: RoutingConstants.InTransit,
      selected: currentPageRoute === RoutingConstants.InTransit,
      title: t('ORDER_AREA_TAB_ORDERS_INTRANSIT', {
        defaultValue: 'Orders InTransit ({{value}})',
        value: inTransitCount
      }),
      value: inTransitCount || 0
    };
  }, [currentPageRoute, t, logEvent, goTo, inTransitCount]);

  const bookedItemsTile = useMemo(() => {
    return {
      onClick: () => {
        logEvent('booked-items');
        goTo(RoutingConstants.OrderHistory);
      },
      key: RoutingConstants.OrderHistory,
      selected: currentPageRoute === RoutingConstants.OrderHistory,
      title: t('ORDER_AREA_TAB_ORDER_HISTORY', {
        defaultValue: 'Order History ({{value}})',
        value: bookedCount || 0
      }),
      value: bookedCount
    };
  }, [currentPageRoute, t, logEvent, goTo, bookedCount]);

  const archivedOrdersTile = useMemo(() => {
    return {
      onClick: () => {
        logEvent('archived-orders');
        goTo(RoutingConstants.ArchivedOrders);
      },
      key: RoutingConstants.ArchivedOrders,
      selected: currentPageRoute === RoutingConstants.ArchivedOrders,
      title: t('ORDER_AREA_TAB_ARCHIVED_ORDERS', { defaultValue: 'Archived Orders' })
    };
  }, [currentPageRoute, t, logEvent, goTo]);

  const sanitisedRoutesOfOrdersMemo = useMemo(() => sanitisedRoutesOfOrders(), []);

  const tiles = useMemo(() => {
    return ([soldItemsTile, inTransitTile, bookedItemsTile, archivedOrdersTile] as P2GDashboardTabProps[])
      .filter((x) => sanitisedRoutesOfOrdersMemo.includes(x.key))
      .map((x, i) => ({
        ...x,
        index: i
      }));
  }, [soldItemsTile, inTransitTile, bookedItemsTile, archivedOrdersTile, sanitisedRoutesOfOrdersMemo]);

  return (
    <RenderSection
      icon={<SvgParcel />}
      title={t('SIDEBAR_ORDERS_SECTION_TITLE', { defaultValue: 'Orders' })}
      links={tiles.map((link) => (
        <SuperLink key={link.key} onClick={link?.onClick} isSelected={link.selected} label={link.title} />
      ))}
      openAsDefault={false}
      storageKey="xs-nav-orders-section-state"
    />
  );
};
export default OrderNavigationSection;
